import React from 'react';
import { QuoteData, formatCurrency, formatEmailCount } from '../../utils/pricingCalculator';
import MailercloudLogo from '../MailercloudLogo';
import './Quote.css';

interface QuoteGeneratorProps {
  quoteData: QuoteData;
}

const QuoteGenerator: React.FC<QuoteGeneratorProps> = ({ quoteData }) => {
  const {
    quoteNumber,
    quoteDate,
    customerName,
    companyName,
    emailVolume,
    pricing,
    discountReason,
    generatedBy
  } = quoteData;

  return (
    <div className="quote-document">
      {/* Header Section */}
      <div className="quote-header">
        <div className="quote-logo">
          <MailercloudLogo size="lg" variant="horizontal" hideSubtext={true} />
        </div>
        <div className="quote-info">
          <h1 className="quote-title">QUOTATION</h1>
          <div className="quote-meta">
            <div className="quote-number">Quote #: {quoteNumber}</div>
            <div className="quote-date">Date: {quoteDate}</div>
          </div>
        </div>
      </div>

      {/* From/To Section */}
      <div className="quote-parties">
        <div className="quote-from">
          <h3>FROM:</h3>
          <div className="company-info">
            <div className="company-name">Mailercloud</div>
            <div className="company-tagline">Email Marketing Solutions</div>
            <div className="company-address">
              <div>Hamilton House, Mabledon Place</div>
              <div>London, Greater London, WC1H 9BB</div>
              <div>United Kingdom</div>
            </div>
            <div className="company-contact">
              <div>📞 +44 20 3890 9520</div>
              <div>📧 support@mailercloud.com</div>
              <div>🌐 www.mailercloud.com</div>
            </div>
            {generatedBy && (
              <div className="generated-by">
                <div><strong>Generated by:</strong></div>
                <div>{generatedBy.name}</div>
                <div>{generatedBy.email}</div>
              </div>
            )}
          </div>
        </div>

        <div className="quote-to">
          <h3>TO:</h3>
          <div className="customer-info">
            {customerName && <div className="customer-name">{customerName}</div>}
            {companyName && <div className="customer-company">{companyName}</div>}
            {!customerName && !companyName && (
              <div className="customer-placeholder">
                <div className="customer-name">Valued Customer</div>
                <div className="customer-note">Please provide customer details for personalized quote</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Details Section */}
      <div className="quote-services">
        <h3>SERVICE DETAILS</h3>
        <div className="service-table">
          <div className="service-header">
            <div className="service-col">Service</div>
            <div className="service-col">Volume</div>
            <div className="service-col">Rate</div>
            <div className="service-col">Amount</div>
          </div>
          <div className="service-row">
            <div className="service-col">
              <div className="service-name">Marketing Emails Plan</div>
              <div className="service-desc">{pricing.slab.label}</div>
            </div>
            <div className="service-col">
              <div className="service-volume">{formatEmailCount(emailVolume)}</div>
              <div className="service-lakhs">({pricing.emailsInLakhs} lakh)</div>
            </div>
            <div className="service-col">
              <div className="service-rate">{formatCurrency(pricing.slab.pricePerLakh, pricing.currency)}</div>
              <div className="service-unit">per lakh</div>
            </div>
            <div className="service-col">
              <div className="service-amount">{formatCurrency(pricing.monthlyPrice, pricing.currency)}</div>
              <div className="service-period">per month</div>
            </div>
          </div>
          
          {/* Discount Row */}
          {pricing.discountPercentage > 0 && (
            <div className="service-row discount-row">
              <div className="service-col">
                <div className="service-name">Discount ({pricing.discountPercentage}%)</div>
                {discountReason && (
                  <div className="service-desc">
                    Reason: {discountReason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                )}
              </div>
              <div className="service-col">-</div>
              <div className="service-col">Applied to email costs</div>
              <div className="service-col">
                <div className="service-amount discount-amount">-{formatCurrency(pricing.discountAmount, pricing.currency)}</div>
                <div className="service-period">discount</div>
              </div>
            </div>
          )}
        </div>

        {/* Calculation Breakdown */}
        <div className="quote-calculation">
          <div className="calculation-line">
            <span>Email Cost:</span>
            <span>{pricing.emailsInLakhs} lakh × {formatCurrency(pricing.slab.pricePerLakh, pricing.currency)} = {formatCurrency(pricing.monthlyPrice, pricing.currency)}</span>
          </div>
          {pricing.discountPercentage > 0 && (
            <>
              <div className="calculation-line discount-line">
                <span>Discount ({pricing.discountPercentage}%):</span>
                <span>-{formatCurrency(pricing.discountAmount, pricing.currency)}</span>
              </div>
              <div className="calculation-line final-line">
                <span>Final Amount:</span>
                <span>{formatCurrency(pricing.discountedPrice, pricing.currency)}</span>
              </div>
            </>
          )}
        </div>

        {/* Total Section */}
        <div className="quote-total">
          <div className="total-line">
            <span className="total-label">Monthly Total:</span>
            <span className="total-amount">
              {formatCurrency(pricing.discountPercentage > 0 ? pricing.discountedPrice : pricing.monthlyPrice, pricing.currency)}
            </span>
          </div>
          {pricing.discountPercentage > 0 && (
            <div className="total-savings">
              <span>You save: {formatCurrency(pricing.discountAmount, pricing.currency)} per month</span>
            </div>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div className="quote-features">
        <h3>INCLUDED FEATURES</h3>
        <div className="features-grid">
          <div className="feature-item">✓ Advanced email editor & templates</div>
          <div className="feature-item">✓ Automation workflows</div>
          <div className="feature-item">✓ Detailed analytics & reporting</div>
          <div className="feature-item">✓ A/B testing capabilities</div>
          <div className="feature-item">✓ 24/7 customer support</div>
          <div className="feature-item">✓ API access & integrations</div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="quote-footer">
        <div className="quote-terms">
          <h4>Terms & Conditions:</h4>
          <ul>
            <li>This quotation is valid for 30 days from the date of issue</li>
            <li>Taxes will be applicable as per government standards</li>
            <li>Payment terms: Monthly subscription billing</li>
            <li>Setup and onboarding support included</li>
          </ul>
        </div>

        <div className="quote-contact">
          <h4>Questions?</h4>
          <p>Contact our sales team for any queries or customizations.</p>
          <div className="contact-details">
            <div>📧 sales@mailercloud.com</div>
            <div>📞 +44 20 3890 9520</div>
            <div>🌐 www.mailercloud.com</div>
          </div>
        </div>

        <div className="quote-signature">
          <div className="signature-line">
            <div>Authorized Signature</div>
            <div className="signature-space"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteGenerator;